import {
  AvailabilityStatus,
  AvailabilityType,
} from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import type { DateTime } from 'luxon';
import { DateTime as LuxonDateTime } from 'luxon';

import { DEFAULT_TIMEZONE } from '../constants';
import { toZonedDateTime } from '../time';

import type {
  AvailabilityStatusTextAvailability,
  ResolvedItemAvailability,
  ResolvedItemAvailabilityParams,
  SelectedTimeSlot,
  TimeSlotAvailabilityParams,
  TimestampRange,
  WeeklyScheduleOptions,
} from './availabilityTypes';
import { isAvailableForWeeklySchedule } from './weeklyScheduleAvailability';

const toAvailabilityDateTime = (
  value: Date | string | null | undefined,
  timezone = DEFAULT_TIMEZONE,
): DateTime | undefined => {
  return toZonedDateTime(value ?? undefined, timezone || DEFAULT_TIMEZONE);
};

const isAvailableForTimestampRanges = (
  ranges: TimestampRange[] | null | undefined,
  selectedSlotStart: DateTime,
  timezone: string,
) => {
  if (!ranges?.length) {
    return false;
  }

  return ranges.some((range) => {
    const startTime = toAvailabilityDateTime(range.startTime, timezone);
    const endTime = toAvailabilityDateTime(range.endTime, timezone);

    return startTime && endTime
      ? selectedSlotStart.toMillis() >= startTime.toMillis() &&
          selectedSlotStart.toMillis() <= endTime.toMillis()
      : false;
  });
};

export const hasFutureAvailability = (
  availability: AvailabilityStatusTextAvailability,
  timezone = DEFAULT_TIMEZONE,
): boolean => {
  if (!availability) {
    return false;
  }

  const zone = timezone || DEFAULT_TIMEZONE;

  if (availability.type === AvailabilityType.TIMESTAMP_RANGES) {
    const ranges = availability.timestampRangesOptions?.ranges;

    if (!ranges?.length) {
      return false;
    }

    const today = LuxonDateTime.local({ zone }).startOf('day').toMillis();

    return ranges.some((range) => {
      const endTime = toZonedDateTime(range.endTime ?? undefined, zone);

      return endTime ? endTime.toMillis() >= today : false;
    });
  }

  if (availability.type === AvailabilityType.WEEKLY_SCHEDULE) {
    const weeklyScheduleOptions = availability.weeklyScheduleOptions as
      | WeeklyScheduleOptions
      | undefined;

    return !!(
      weeklyScheduleOptions?.weeklyAvailability ||
      weeklyScheduleOptions?.availableTimes?.length
    );
  }

  return availability.type === AvailabilityType.ALWAYS_AVAILABLE;
};

export const isAvailabilityAvailableForTimeSlot = ({
  availability,
  selectedTimeSlot,
  timezone = DEFAULT_TIMEZONE,
}: TimeSlotAvailabilityParams): boolean => {
  if (
    !availability ||
    availability.type === AvailabilityType.ALWAYS_AVAILABLE
  ) {
    return true;
  }

  const zone = timezone || DEFAULT_TIMEZONE;
  const selectedSlotStart = toAvailabilityDateTime(
    selectedTimeSlot?.startTime,
    zone,
  );

  if (!selectedSlotStart) {
    return true;
  }

  if (availability.type === AvailabilityType.TIMESTAMP_RANGES) {
    return isAvailableForTimestampRanges(
      availability.timestampRangesOptions?.ranges,
      selectedSlotStart,
      zone,
    );
  }

  if (availability.type === AvailabilityType.WEEKLY_SCHEDULE) {
    const weeklyScheduleOptions = availability.weeklyScheduleOptions as
      | WeeklyScheduleOptions
      | undefined;

    return isAvailableForWeeklySchedule({
      weeklyScheduleOptions,
      selectedSlotStart,
      timezone: zone,
    });
  }

  return false;
};

export const getAvailabilityStatusForTimeSlot = (
  params: TimeSlotAvailabilityParams,
): AvailabilityStatus =>
  isAvailabilityAvailableForTimeSlot(params)
    ? AvailabilityStatus.AVAILABLE
    : AvailabilityStatus.UNAVAILABLE;

type AvailabilitySource = {
  availability?: AvailabilityStatusTextAvailability;
  availabilityStatus?: AvailabilityStatus;
};

const isAvailabilitySourceAvailable = ({
  availability,
  availabilityStatus,
  selectedTimeSlot,
  timezone,
}: AvailabilitySource & {
  selectedTimeSlot?: SelectedTimeSlot | null;
  timezone?: string;
}) => {
  if (availabilityStatus !== undefined) {
    return availabilityStatus !== AvailabilityStatus.UNAVAILABLE;
  }

  return isAvailabilityAvailableForTimeSlot({
    availability,
    selectedTimeSlot,
    timezone,
  });
};

const isItemBlockedForFulfillment = ({
  item,
  selectedDispatchType,
}: Pick<ResolvedItemAvailabilityParams, 'item' | 'selectedDispatchType'>) =>
  !!selectedDispatchType &&
  !!item.orderSettings?.blockedFulfillmentMethods?.some(
    (method) => String(method) === selectedDispatchType,
  );

export const getResolvedItemAvailability = ({
  item,
  menuAvailability,
  menuAvailabilityStatus,
  sectionAvailability,
  sectionAvailabilityStatus,
  selectedTimeSlot,
  selectedDispatchType,
  timezone,
}: ResolvedItemAvailabilityParams): ResolvedItemAvailability => {
  const unavailableTimeBasedSource = [
    {
      availability: menuAvailability,
      availabilityStatus: menuAvailabilityStatus,
    },
    {
      availability: sectionAvailability,
      availabilityStatus: sectionAvailabilityStatus,
    },
    { availability: item.orderSettings?.availability },
  ].find(
    (source) =>
      !isAvailabilitySourceAvailable({
        ...source,
        selectedTimeSlot,
        timezone,
      }),
  );
  const isTimeBasedAvailable = !unavailableTimeBasedSource;
  const isBlockedForFulfillment = isItemBlockedForFulfillment({
    item,
    selectedDispatchType,
  });
  const isAvailable =
    isTimeBasedAvailable &&
    item.orderSettings?.inStock !== false &&
    !isBlockedForFulfillment;
  const sourceAvailability = unavailableTimeBasedSource?.availability;
  const result = {
    isAvailable,
    isTimeBasedAvailable,
    isBlockedForFulfillment,
  };

  return sourceAvailability
    ? { ...result, availability: sourceAvailability }
    : result;
};
